"use client";

import { useParams, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Script from "next/script";
import { DateInput } from "@/app/components/app/DateInput";
import type { DbSiteConfig, DbZone } from "@/lib/server-db";
import { PROPERTY_FEATURE_GROUPS } from "@/lib/website/property-features";
import { propertyPublicSlug } from "@/lib/property-slug";
import { publicSitePath } from "@/lib/website/public-path";
import { isPublicUploadUrl } from "@/lib/website/public-image";
import { serializeJsonLd } from "@/lib/website/json-ld";

function localIsoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type SiteProperty = {
  id: string;
  slug?: string;
  name: string;
  city: string;
  country: string;
  type: string;
  bedrooms: number;
  beds?: number;
  bathrooms?: number;
  capacity: number;
  headline?: string;
  description?: string;
  description_en?: string;
  description_de?: string;
  baseRate?: number;
  photos: string[];
  amenities?: string[];
  houseRules?: string[];
  checkInFrom?: string;
  checkOutUntil?: string;
  cancellationPolicy?: string;
};

type PaymentConfig = {
  hasStripe: boolean;
  stripePublishableKey: string | null;
  hasBankTransfer: boolean;
  iban: string | null;
  bankAccountName: string | null;
};

export type SiteData = {
  agency: { id: string; name?: string };
  siteOrigin?: string;
  config: DbSiteConfig | null;
  properties: SiteProperty[];
  zones: DbZone[];
  paymentConfig: PaymentConfig;
};

type Lang = "es" | "en" | "de";

function getLangFlag(l: string): string {
  if (l === "es") return "🇪🇸";
  if (l === "en") return "🇬🇧";
  if (l === "de") return "🇩🇪";
  return l.toUpperCase();
}

function getPropDesc(p: SiteProperty, lang: Lang): string | undefined {
  if (lang === "en" && p.description_en) return p.description_en;
  if (lang === "de" && p.description_de) return p.description_de;
  return p.description;
}

function PropertyStat({ icon, value, label }: { icon: string; value: number; label: string }) {
  if (!value) return null;
  return <div className="site-propdetail-stat"><span aria-hidden="true" className="site-propdetail-stat-icon">{icon}</span><span><strong>{value}</strong><small>{label}</small></span></div>;
}

// Booking steps
type Step = "idle" | "form" | "stripe_payment" | "bank_transfer_done" | "stripe_done" | "request_done";

export default function PropertyDetailClient({ initialData }: { initialData?: SiteData }) {
  const { agencyId, propertyId } = useParams<{ agencyId: string; propertyId: string }>();
  const searchParams = useSearchParams();
  const [data, setData] = useState<SiteData | null>(initialData ?? null);
  const [loading, setLoading] = useState(!initialData);
  const [lang, setLang] = useState<Lang>((initialData?.config?.languages?.[0] as Lang | undefined) ?? "es");

  // Dates
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");

  // Lightbox
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Booking form
  const [step, setStep] = useState<Step>("idle");
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "bank_transfer">(initialData?.paymentConfig?.hasStripe ? "stripe" : "bank_transfer");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestCount, setGuestCount] = useState(2);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Stripe state
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [reservationCode, setReservationCode] = useState<string | null>(null);
  const [reservationToken, setReservationToken] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const [quote, setQuote] = useState<{ totalAmount: number; stayAmount: number; cityTaxAmount: number; excludedTaxAmount: number; taxes: { name: string; amount: number; chargedSeparately: boolean }[]; nights: number; pricePerNight: number } | null>(null);

  // Bank transfer state
  const [bankIban, setBankIban] = useState<string | null>(null);
  const [bankAccountName, setBankAccountName] = useState<string | null>(null);

  const stripeElementsRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<VilatioStripeInstance & { _elements?: VilatioStripeElements } | null>(null);
  const reservationKeyRef = useRef<{ fingerprint: string; key: string } | null>(null);

  function reservationIdempotencyKey() {
    const fingerprint = JSON.stringify({ propertyId: property?.id, checkin, checkout, guests: guestCount, guestName, guestEmail, guestPhone, paymentMethod, notes });
    if (reservationKeyRef.current?.fingerprint !== fingerprint) reservationKeyRef.current = { fingerprint, key: crypto.randomUUID() };
    return reservationKeyRef.current.key;
  }

  useEffect(() => {
    if (initialData) return;
    fetch(`/api/site/${agencyId}`)
      .then((r) => r.json())
      .then((json) => {
        const d = json as SiteData;
        setData(d);
        const langs = d.config?.languages ?? ["es"];
        if (langs.length > 0) setLang(langs[0] as Lang);
        if (d.paymentConfig?.hasStripe) setPaymentMethod("stripe");
        else if (d.paymentConfig?.hasBankTransfer) setPaymentMethod("bank_transfer");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [agencyId, initialData]);

  const property = data?.properties.find((p) => p.id === propertyId || p.slug === propertyId);
  const config = data?.config;
  const brandName = config?.brandName || data?.agency?.name || "Vilatio";
  const pc = data?.paymentConfig;
  const nightsCalc = checkin && checkout
    ? Math.round((new Date(checkout).getTime() - new Date(checkin).getTime()) / 86400000)
    : 0;

  useEffect(() => {
    const from = searchParams.get("dateFrom") || searchParams.get("checkin") || "";
    const to = searchParams.get("dateTo") || searchParams.get("checkout") || "";
    const guests = Number(searchParams.get("guests") || 0);
    // URL search parameters are an external source of truth for the booking widget.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (from) setCheckin(from);
    if (to) setCheckout(to);
    if (guests > 0) setGuestCount(Math.max(1, guests));
  }, [searchParams]);

  useEffect(() => {
    if (!property || !checkin || !checkout || nightsCalc <= 0) {
      // Clear stale pricing while the requested dates are incomplete.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuote(null);
      return;
    }
    const controller = new AbortController();
    fetch(`/api/reservations/${agencyId}/quote?propertyId=${encodeURIComponent(property.id)}&dateFrom=${encodeURIComponent(checkin)}&dateTo=${encodeURIComponent(checkout)}&guests=${guestCount}`, { signal: controller.signal })
      .then((response) => response.json())
      .then((result) => setQuote(result.available ? result : null))
      .catch(() => {});
    return () => controller.abort();
  }, [agencyId, property, checkin, checkout, guestCount, nightsCalc]);

  useEffect(() => {
    if (property) document.title = `${property.name} | ${brandName}`;
  }, [property, brandName]);

  // Lightbox keyboard nav
  useEffect(() => {
    if (lightboxIndex === null) return;
    const photos = property?.photos ?? [];
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setLightboxIndex((i) => i !== null ? (i + 1) % photos.length : null);
      if (e.key === "ArrowLeft") setLightboxIndex((i) => i !== null ? (i - 1 + photos.length) % photos.length : null);
      if (e.key === "Escape") setLightboxIndex(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [lightboxIndex, property]);

  // Mount Stripe Elements when clientSecret is available
  useEffect(() => {
    if (step !== "stripe_payment" || !stripeClientSecret || !stripePublishableKey) return;
    if (!window.Stripe) return;

    const stripe = window.Stripe(stripePublishableKey);
    stripeRef.current = stripe;
    const elements = stripe.elements({ clientSecret: stripeClientSecret, locale: lang });
    const paymentElement = elements.create("payment");
    if (stripeElementsRef.current) {
      stripeElementsRef.current.innerHTML = "";
      paymentElement.mount(stripeElementsRef.current);
    }

    // Store elements for later confirm
    stripeRef.current._elements = elements;
  }, [step, stripeClientSecret, stripePublishableKey, lang]);

  async function handleReserve(e: React.FormEvent) {
    e.preventDefault();
    if (!property) return;
    setBookingError(null);
    setSubmitting(true);
    try {
      if (!hasPayment) {
        const requestResponse = await fetch(`/api/booking-requests/${agencyId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ propertyId: property.id, checkin, checkout, guests: guestCount, guestName, guestEmail, guestPhone, message: notes }),
        });
        const requestJson = await requestResponse.json();
        if (!requestResponse.ok) throw new Error(requestJson.error ?? "No se pudo enviar la solicitud");
        setReservationCode(requestJson.id ? `SOL-${String(requestJson.id).slice(0, 8).toUpperCase()}` : null);
        setStep("request_done");
        return;
      }
      const res = await fetch(`/api/reservations/${agencyId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": reservationIdempotencyKey() },
        body: JSON.stringify({
          propertyId: property.id,
          checkin,
          checkout,
          guests: guestCount,
          guestName,
          guestEmail,
          guestPhone,
          paymentMethod,
          notes,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Error al crear la reserva");

      setReservationId(json.reservationId);
      setReservationCode(json.reservationCode);
      setReservationToken(json.reservationToken ?? null);
      setTotalAmount(json.totalAmount);

      if (paymentMethod === "bank_transfer") {
        setBankIban(json.iban);
        setBankAccountName(json.bankAccountName);
        setStep("bank_transfer_done");
      } else {
        setStripeClientSecret(json.clientSecret);
        setStripePublishableKey(json.stripePublishableKey);
        setStep("stripe_payment");
      }
    } catch (err) {
      setBookingError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStripeConfirm(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setBookingError(null);
    try {
      const stripe = stripeRef.current;
      if (!stripe?._elements) throw new Error("El formulario de pago aún no está listo");
      const elements = stripe._elements;
      const returnUrl = `${window.location.origin}${publicSitePath(agencyId, `/reserva/${reservationId}`, config?.domain)}?token=${encodeURIComponent(reservationToken ?? "")}`;
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
      });
      if (error) throw new Error(error.message);
      // If no redirect (unusual), set done
      setStep("stripe_done");
    } catch (err) {
      setBookingError(err instanceof Error ? err.message : "Error en el pago");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="site-loading"><div className="site-loading-dot" /></div>;
  }
  if (!data || !property) {
    return <div className="site-not-found">Propiedad no encontrada</div>;
  }

  const accent = config?.accentColor || "#2563EB";
  const languages = config?.languages?.length ? config.languages : ["es"];
  const desc = getPropDesc(property, lang);
  const photos = property.photos ?? [];
  const groupedAmenities = PROPERTY_FEATURE_GROUPS.map((group) => ({ ...group, selected: (property.amenities ?? []).filter((amenity) => group.options.includes(amenity)) })).filter((group) => group.selected.length > 0);

  const hasPayment = Boolean(pc?.hasStripe || pc?.hasBankTransfer);

  const baseUrl = data.siteOrigin?.replace(/\/+$/, "")
    || (config?.domain ? `https://${config.domain}` : (typeof window !== "undefined" ? window.location.origin : ""));

  const propertySchema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": property.name,
    "url": `${baseUrl}${config?.domain ? `/propiedad/${propertyPublicSlug(property)}` : `/site/${agencyId}/propiedad/${propertyPublicSlug(property)}`}`,
    ...(desc ? { "description": desc } : {}),
    "address": { "@type": "PostalAddress", "addressLocality": property.city, "addressCountry": property.country || "ES" },
    "numberOfRooms": property.bedrooms,
    "occupancy": { "@type": "QuantitativeValue", "maxValue": property.capacity },
    ...(photos.length > 0 ? { "photo": photos.slice(0, 6).map((p) => ({ "@type": "ImageObject", "url": `${baseUrl}/api/uploads/${p}?w=1200` })) } : {}),
    ...(property.baseRate ? { "offers": { "@type": "Offer", "price": property.baseRate, "priceCurrency": "EUR", "availability": "https://schema.org/InStock" } } : {}),
  };
  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": config?.brandName || data.agency.name || "Inicio", "item": `${baseUrl}${publicSitePath(agencyId, "", config?.domain)}` },
      ...(property.city ? [{ "@type": "ListItem", "position": 2, "name": property.city }] : []),
      { "@type": "ListItem", "position": property.city ? 3 : 2, "name": property.name, "item": `${baseUrl}${publicSitePath(agencyId, `/propiedad/${propertyPublicSlug(property)}`, config?.domain)}` },
    ],
  };

  return (
    <div className="site-root" style={{ "--site-accent": accent } as React.CSSProperties}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd({ "@context": "https://schema.org", "@graph": [propertySchema, breadcrumbSchema] }) }} />
      {pc?.hasStripe && (
        <Script src="https://js.stripe.com/v3/" strategy="lazyOnload" />
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && photos.length > 0 && (
        <div className="site-lightbox" onClick={() => setLightboxIndex(null)}>
          <button aria-label="Cerrar galería" className="site-lightbox-close" onClick={() => setLightboxIndex(null)} type="button">×</button>
          {photos.length > 1 && (
            <button aria-label="Imagen anterior" className="site-lightbox-nav site-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => i !== null ? (i - 1 + photos.length) % photos.length : 0); }} type="button">‹</button>
          )}
          <Image width={1600} height={1000} sizes="100vw" unoptimized src={`/api/uploads/${photos[lightboxIndex]}?w=1600`} alt={property.name} onClick={(e) => e.stopPropagation()} />
          {photos.length > 1 && (
            <button aria-label="Imagen siguiente" className="site-lightbox-nav site-lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => i !== null ? (i + 1) % photos.length : 0); }} type="button">›</button>
          )}
        </div>
      )}

      <header className="site-header">
        <div className="site-header-inner">
          <a aria-label={brandName} className="site-brand" href={publicSitePath(agencyId, "", config?.domain)}>
            {config?.logoImage
               ? <Image alt={brandName} className="site-logo-img" width={160} height={40} unoptimized src={`/api/uploads/${config.logoImage}?w=320`} />
              : <span>{brandName}</span>}
          </a>
          <div className="site-header-right">
            {languages.length > 1 && (
              <div className="site-lang-toggle">
                {languages.map((l) => (
                  <button aria-label={`Cambiar idioma a ${l}`} aria-pressed={lang === l} key={l} className={`site-lang-btn${lang === l ? " is-active" : ""}`}
                    onClick={() => setLang(l as Lang)} type="button">{getLangFlag(l)}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="site-propdetail">
        <a className="site-propdetail-back" href={publicSitePath(agencyId, "", config?.domain)}>← {brandName}</a>

        <h1 className="site-propdetail-title">{property.name}</h1>
        {property.headline && <p className="site-propdetail-headline">{property.headline}</p>}
        <div className="site-propdetail-stats" aria-label="Datos del alojamiento" role="group">
          <PropertyStat icon="🛏" value={property.bedrooms} label="dormitorios" />
          <PropertyStat icon="🛁" value={property.bathrooms ?? 0} label="baños" />
          <PropertyStat icon="👥" value={property.capacity} label="huéspedes" />
        </div>
        <p className="site-propdetail-meta">
          {property.city}
          {property.bedrooms ? ` · ${property.bedrooms} dormitorios` : ""}
          {property.capacity ? ` · ${property.capacity} huéspedes` : ""}
        </p>

        {/* Gallery */}
        {photos.length === 0 ? (
          <div className="site-propdetail-gallery"><div className="site-propdetail-no-photos">Sin fotos disponibles</div></div>
        ) : photos.length === 1 ? (
          <div className="site-propdetail-gallery">
            <div className="site-propdetail-main">
               <Image width={1200} height={800} fetchPriority="high" preload sizes="(max-width: 800px) 100vw, 70vw" unoptimized={isPublicUploadUrl(`/api/uploads/${photos[0]}?w=1200`)} alt={property.name} src={`/api/uploads/${photos[0]}?w=1200`} onClick={() => setLightboxIndex(0)} />
            </div>
          </div>
        ) : (
          <div className="site-propdetail-gallery">
            <div className="site-propdetail-main">
               <Image width={1200} height={800} fetchPriority="high" preload sizes="(max-width: 800px) 100vw, 70vw" unoptimized={isPublicUploadUrl(`/api/uploads/${photos[0]}?w=1200`)} alt={property.name} src={`/api/uploads/${photos[0]}?w=1200`} onClick={() => setLightboxIndex(0)} />
            </div>
            <div className="site-propdetail-side">
              {[1, 2, 3, 4].map((idx) => (
                photos[idx] ? (
                  <div className="site-propdetail-side-photo" key={idx}>
                    <Image width={600} height={400} loading="lazy" sizes="(max-width: 800px) 50vw, 25vw" unoptimized={isPublicUploadUrl(`/api/uploads/${photos[idx]}?w=600`)} alt={property.name} src={`/api/uploads/${photos[idx]}?w=600`} onClick={() => setLightboxIndex(idx)} />
                  </div>
                ) : (
                  <div className="site-propdetail-side-photo" key={idx} style={{ background: "#F1F5F9" }} />
                )
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="site-propdetail-body">
          <div className="site-propdetail-desc">
            {desc ? <p className="site-propdetail-description">{desc}</p> : <p style={{ color: "var(--muted)" }}>Sin descripción disponible.</p>}
            {(property.amenities ?? []).length > 0 && (
              <div className="site-propdetail-amenities">
                <h2>Características</h2>
                {groupedAmenities.map((group) => <section className="site-propdetail-amenity-group" key={group.key}><h3>{group.label}</h3><div>{group.selected.map((amenity) => <span key={amenity}>{amenity}</span>)}</div></section>)}
              </div>
            )}
            {(property.houseRules ?? []).filter(Boolean).length > 0 && <div className="site-propdetail-info-card"><h2>Normas de la casa</h2><ul>{property.houseRules?.filter(Boolean).map((rule) => <li key={rule}>{rule}</li>)}</ul></div>}
            {(property.checkInFrom || property.checkOutUntil || property.cancellationPolicy) && <div className="site-propdetail-info-card"><h2>Información importante</h2>{property.checkInFrom && <p><strong>Entrada:</strong> desde las {property.checkInFrom}</p>}{property.checkOutUntil && <p><strong>Salida:</strong> hasta las {property.checkOutUntil}</p>}{property.cancellationPolicy && <p><strong>Cancelación:</strong> {property.cancellationPolicy}</p>}</div>}
          </div>

          {/* Booking widget */}
          <div className="site-propdetail-booking">
            {property.baseRate ? (
              <p className="site-propdetail-booking-price">
                <strong>{property.baseRate}€</strong> <span>/ noche</span>
              </p>
            ) : (
              <p className="site-propdetail-booking-price">Consulta precio</p>
            )}

            <div className="site-propdetail-date-row">
              <div className="site-propdetail-date-field">
                <label htmlFor="prop-checkin">Entrada</label>
                <DateInput value={checkin}
                  ariaLabel="Fecha de entrada"
                  min={localIsoDate(new Date())}
                  onChange={(v) => { setCheckin(v); if (checkout && v >= checkout) setCheckout(""); }} />
              </div>
              <div className="site-propdetail-date-field">
                <label htmlFor="prop-checkout">Salida</label>
                <DateInput value={checkout}
                  ariaLabel="Fecha de salida"
                  min={checkin || localIsoDate(new Date())}
                  onChange={setCheckout} />
              </div>
            </div>

            {quote && (
              <div className="site-propdetail-total">
                <span>{quote.nights} noches · {quote.pricePerNight.toLocaleString("es-ES")}€/noche</span>
                <span>Estancia <strong>{quote.stayAmount.toLocaleString("es-ES")}€</strong></span>
                {quote.taxes.length > 0 ? quote.taxes.map((tax) => <small key={`${tax.name}-${tax.amount}`}>{tax.name}: {tax.amount.toLocaleString("es-ES")}€{tax.chargedSeparately ? " · se abona aparte" : ""}</small>) : <small>Impuestos turísticos, si aplican, se abonan aparte.</small>}
              </div>
            )}

            {step === "idle" && checkin && checkout && nightsCalc > 0 && quote && (
              <button className="site-propdetail-cta" style={{ background: accent }} type="button"
                onClick={() => { setStep("form"); setTimeout(() => document.getElementById("reserva-form")?.scrollIntoView({ behavior: "smooth" }), 50); }}>
                Reservar ahora
              </button>
            )}
            {false && (
              <a className="site-propdetail-cta" href="#contacto-propiedad" style={{ background: accent }}
                onClick={(e) => { e.preventDefault(); document.getElementById("contacto-propiedad")?.scrollIntoView({ behavior: "smooth" }); }}>
                Solicitar información
              </a>
            )}
          </div>
        </div>

        {/* ── BOOKING FORM ── */}
        {step !== "idle" && (
          <div id="reserva-form" className="site-reserva-section">

            {/* Step: guest info + payment method */}
            {step === "form" && (
              <form className="site-reserva-form" onSubmit={handleReserve}>
                <h2 className="site-reserva-title">Completa tu reserva</h2>

                {/* Summary */}
                <div className="site-reserva-summary">
                  <div className="site-reserva-summary-prop">{property.name}</div>
                  <div className="site-reserva-summary-dates">{checkin} → {checkout} · {nightsCalc} noches</div>
          {(quote || property.baseRate) && (
                    <div className="site-reserva-summary-total">
                      Total estimado: <strong>{(quote?.totalAmount ?? nightsCalc * (property.baseRate ?? 0)).toLocaleString("es-ES")}€</strong>
                      <span className="site-reserva-summary-note"> (confirmado al procesar)</span>
                    </div>
                  )}
                </div>

                {/* Guest info */}
                <div className="site-reserva-group">
                  <h3>Datos del huésped principal</h3>
                  <div className="site-reserva-row">
                    <div className="site-reserva-field">
                      <label>Nombre completo *</label>
                      <input aria-label="Nombre completo" required type="text" value={guestName} placeholder="Tu nombre"
                        onChange={(e) => setGuestName(e.target.value)} />
                    </div>
                    <div className="site-reserva-field">
                      <label>Email *</label>
                      <input aria-label="Correo electrónico" required type="email" value={guestEmail} placeholder="tu@email.com"
                        onChange={(e) => setGuestEmail(e.target.value)} />
                    </div>
                  </div>
                  <div className="site-reserva-row">
                    <div className="site-reserva-field">
                      <label>Teléfono</label>
                      <input aria-label="Teléfono" type="tel" value={guestPhone} placeholder="+34 600 000 000"
                        onChange={(e) => setGuestPhone(e.target.value)} />
                    </div>
                    <div className="site-reserva-field">
                      <label>Número de huéspedes</label>
                      <input aria-label="Número de huéspedes" type="number" min={1} max={property.capacity || 20} value={guestCount}
                        onChange={(e) => setGuestCount(Number(e.target.value))} />
                    </div>
                  </div>
                  <div className="site-reserva-field">
                    <label>Notas / peticiones especiales</label>
                    <textarea aria-label="Peticiones especiales" rows={2} value={notes} placeholder="Llegada tarde, cuna, alérgenos…"
                      onChange={(e) => setNotes(e.target.value)} />
                  </div>
                </div>

                {/* Payment method */}
                {pc?.hasStripe && pc?.hasBankTransfer ? (
                  <div className="site-reserva-group">
                    <h3>Método de pago</h3>
                    <div className="site-reserva-payment-options">
                      <label className={`site-reserva-payment-opt ${paymentMethod === "stripe" ? "is-selected" : ""}`}>
                        <input type="radio" name="payment" value="stripe" checked={paymentMethod === "stripe"}
                          onChange={() => setPaymentMethod("stripe")} />
                        <span className="site-reserva-payment-opt-icon">💳</span>
                        <span>
                          <strong>Tarjeta de crédito</strong>
                          <small>Pago seguro con Stripe · confirmación inmediata</small>
                        </span>
                      </label>
                      <label className={`site-reserva-payment-opt ${paymentMethod === "bank_transfer" ? "is-selected" : ""}`}>
                        <input type="radio" name="payment" value="bank_transfer" checked={paymentMethod === "bank_transfer"}
                          onChange={() => setPaymentMethod("bank_transfer")} />
                        <span className="site-reserva-payment-opt-icon">🏦</span>
                        <span>
                          <strong>Transferencia bancaria</strong>
                          <small>Recibirás el IBAN · 48h para confirmar</small>
                        </span>
                      </label>
                    </div>
                  </div>
                ) : pc?.hasStripe ? (
                  <div className="site-reserva-payment-badge">💳 Pago con tarjeta (Stripe)</div>
                ) : (
                  <div className="site-reserva-payment-badge">🏦 Transferencia bancaria</div>
                )}

                {!hasPayment && <div className="site-reserva-payment-badge">Solicitud de reserva · te confirmaremos disponibilidad y forma de pago</div>}

                {bookingError && <p className="site-reserva-error">{bookingError}</p>}

                <div className="site-reserva-actions">
                  <button className="site-reserva-btn" style={{ background: accent }} type="submit"
                    disabled={submitting || !guestName || !guestEmail}>
                    {submitting ? "Procesando…" : paymentMethod === "stripe" ? "Continuar al pago →" : "Confirmar reserva →"}
                  </button>
                  <button className="site-reserva-btn-cancel" type="button" onClick={() => setStep("idle")}>
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            {/* Step: Stripe Payment Element */}
            {step === "stripe_payment" && (
              <form className="site-reserva-form" onSubmit={handleStripeConfirm}>
                <h2 className="site-reserva-title">Pago seguro</h2>
                <div className="site-reserva-summary">
                  <div className="site-reserva-summary-prop">{property.name} · {reservationCode}</div>
                  <div className="site-reserva-summary-total">
                    Total: <strong>{totalAmount?.toLocaleString("es-ES")}€</strong>
                  </div>
                </div>
                <div className="site-stripe-element-wrap">
                  <div ref={stripeElementsRef} className="site-stripe-element" />
                </div>
                {bookingError && <p className="site-reserva-error">{bookingError}</p>}
                <div className="site-reserva-actions">
                  <button className="site-reserva-btn" style={{ background: accent }} type="submit" disabled={submitting}>
                    {submitting ? "Procesando…" : `Pagar ${totalAmount?.toLocaleString("es-ES")}€`}
                  </button>
                </div>
                <p className="site-stripe-secure">🔒 Pago procesado de forma segura por Stripe. No almacenamos datos de tu tarjeta.</p>
              </form>
            )}

            {/* Step: bank transfer instructions */}
            {step === "bank_transfer_done" && (
              <div className="site-reserva-form site-reserva-transfer">
                <div className="site-reserva-success-icon">✓</div>
                <h2 className="site-reserva-title">Reserva confirmada — pendiente de pago</h2>
                <p className="site-reserva-transfer-intro">
                  Tu reserva <strong>{reservationCode}</strong> ha sido registrada.<br />
                  Realiza la transferencia en las próximas 48h para confirmar la disponibilidad.
                </p>
                <div className="site-reserva-iban-card">
                  <div className="site-reserva-iban-row">
                    <span>Titular</span>
                    <strong>{bankAccountName}</strong>
                  </div>
                  <div className="site-reserva-iban-row">
                    <span>IBAN</span>
                    <strong className="site-reserva-iban">{bankIban}</strong>
                  </div>
                  <div className="site-reserva-iban-row">
                    <span>Importe</span>
                    <strong>{totalAmount?.toLocaleString("es-ES")}€</strong>
                  </div>
                  <div className="site-reserva-iban-row">
                    <span>Concepto</span>
                    <strong>{reservationCode}</strong>
                  </div>
                </div>
                <p className="site-reserva-transfer-note">
                  Incluye el código <strong>{reservationCode}</strong> en el concepto de la transferencia.<br />
                  Te confirmaremos la reserva por email en {guestEmail} cuando recibamos el pago.
                </p>
              </div>
            )}

            {/* Step: Stripe done (no redirect case) */}
            {step === "stripe_done" && (
              <div className="site-reserva-form site-reserva-transfer">
                <div className="site-reserva-success-icon">✓</div>
                <h2 className="site-reserva-title">¡Reserva confirmada!</h2>
                <p>Tu reserva <strong>{reservationCode}</strong> ha sido pagada y confirmada. Recibirás un email en <strong>{guestEmail}</strong>.</p>
              </div>
            )}

            {step === "request_done" && (
              <div className="site-reserva-form site-reserva-transfer">
                <div className="site-reserva-success-icon">✓</div>
                <h2 className="site-reserva-title">Solicitud recibida</h2>
                <p>La agencia comprobará la disponibilidad y te contactará en <strong>{guestEmail}</strong>.</p>
                <div className="site-reserva-summary">
                  <div className="site-reserva-summary-prop">{property.name}</div>
                  <div className="site-reserva-summary-dates">{checkin} → {checkout} · {nightsCalc} noches · {guestCount} huéspedes</div>
                  {quote && <div className="site-reserva-summary-total">Estancia: <strong>{quote.totalAmount.toLocaleString("es-ES")}€</strong></div>}
                  {reservationCode && <div className="site-reserva-summary-note">Referencia: {reservationCode}</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CONTACT (fallback if no payment) ── */}
        {!hasPayment && (
          <div id="contacto-propiedad" className="site-booking-form-section">
            <h2 className="site-booking-form-title">¿Te interesa este alojamiento?</h2>
            <p className="site-booking-form-sub">Contacta con nosotros para comprobar disponibilidad y reservar.</p>
            {(config?.email || config?.phone) && (
              <div className="site-booking-contacts">
                {config.email && <a href={`mailto:${config.email}`} style={{ color: accent }}>✉ {config.email}</a>}
                {config.phone && <a href={`tel:${config.phone}`} style={{ color: accent }}>☎ {config.phone}</a>}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="site-footer-brand"><strong>{brandName}</strong></div>
          <nav aria-label="Navegación del pie" className="site-footer-nav">
            <a href={publicSitePath(agencyId, "", config?.domain)}>Inicio</a>
            <a href={`${publicSitePath(agencyId, "", config?.domain)}#propiedades`}>Propiedades</a>
            <a href={`${publicSitePath(agencyId, "", config?.domain)}#contacto`}>Contacto</a>
          </nav>
          <p className="site-footer-powered">Powered by <strong>Vilatio</strong></p>
        </div>
      </footer>
    </div>
  );
}
